import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaPaperPlane, FaRobot, FaTimes, FaUser } from 'react-icons/fa';
import { useAiAssistant } from '../context/AiAssistantContext';
import useBookingAssistantChat from '../hooks/useBookingAssistantChat';
import AiAssistantRecommendations from './AiAssistantRecommendations';
import './AiAssistantChatWidget.css';
import '../pages/BookingAssistantPage.css';

const AiAssistantChatWidget = () => {
  const { widgetOpen, closeWidget, consumePendingMessage } = useAiAssistant();
  const { messages, loading, sendMessage, handleBook, goToSearchWithIntent } =
    useBookingAssistantChat({ language: 'en' });
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const pendingHandledRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, widgetOpen]);

  useEffect(() => {
    if (!widgetOpen) {
      pendingHandledRef.current = false;
      return;
    }
    if (pendingHandledRef.current) return;
    const pending = consumePendingMessage();
    if (pending) {
      pendingHandledRef.current = true;
      sendMessage(pending);
    }
  }, [widgetOpen, consumePendingMessage, sendMessage]);

  if (!widgetOpen) return null;

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' && m.intent);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  return createPortal(
    <>
      <button
        type="button"
        className="ai-chat-widget-backdrop"
        aria-label="Close AI assistant"
        onClick={closeWidget}
      />
      <div className="ai-chat-widget" role="dialog" aria-label="AI booking assistant chat">
        <header className="ai-chat-widget-header">
          <div className="ai-chat-widget-brand">
            <FaRobot />
            <div>
              <h3>AI Stay Finder</h3>
              <p>Natural language search</p>
            </div>
          </div>
          <div className="ai-chat-widget-actions">
            {lastAssistant?.intent && (
              <button
                type="button"
                onClick={() =>
                  goToSearchWithIntent(
                    lastAssistant.intent,
                    lastAssistant.content,
                    lastAssistant.recommendations
                  )
                }
              >
                View in search
              </button>
            )}
            <Link to="/ai-assistant" onClick={closeWidget}>
              Full page
            </Link>
            <button type="button" onClick={closeWidget} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="ai-chat-widget-messages">
          {messages.map((msg, idx) => (
            <div key={`${msg.role}-${idx}`} className={`ai-msg ai-msg-${msg.role}`}>
              <span className={`ai-msg-avatar ai-msg-avatar--${msg.role}`} aria-hidden="true">
                {msg.role === 'assistant' ? <FaRobot /> : <FaUser />}
              </span>
              <div className="ai-msg-content">
                <div className="ai-msg-bubble">{msg.content}</div>
                {msg.recommendations?.length > 0 && (
                  <AiAssistantRecommendations
                    recommendations={msg.recommendations}
                    intent={msg.intent}
                    onBook={handleBook}
                    compact
                  />
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="ai-msg ai-msg-assistant">
              <span className="ai-msg-avatar ai-msg-avatar--assistant" aria-hidden="true">
                <FaRobot />
              </span>
              <div className="ai-msg-bubble ai-chat-widget-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form className="ai-chat-widget-composer" onSubmit={handleSubmit}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about stays or meeting rooms…"
            aria-label="Message to AI assistant"
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()} aria-label="Send">
            <FaPaperPlane />
          </button>
        </form>
      </div>
    </>,
    document.body
  );
};

export default AiAssistantChatWidget;
